import { useState, useEffect } from "react";
import {
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Box,
  Paper,
  Typography,
} from "@mui/material";

const MatchEventForm = ({
  homeTeam,
  awayTeam,
  isCountryMatch = false,
  homeSideLabel,
  awaySideLabel,
  homeCountryCode,
  awayCountryCode,
  editingEvent = null,
  onSubmit,
  onCancel,
  isSubmitting,
}) => {
  const [formValues, setFormValues] = useState({
    teamId: "",
    country: "",
    time: "",
    type: "goal",
    playerName: "",
    assistName: "",
    order: "",
  });

  useEffect(() => {
    if (editingEvent) {
      setFormValues({
        teamId: editingEvent.teamId || "",
        country: editingEvent.country || "",
        time: editingEvent.time || "",
        type: editingEvent.type || "goal",
        playerName: editingEvent.playerName || "",
        assistName: editingEvent.assistName || "",
        order: editingEvent.order || "",
      });
    } else {
      setFormValues({
        teamId: "",
        country: "",
        time: "",
        type: "goal",
        playerName: "",
        assistName: "",
        order: "",
      });
    }
  }, [editingEvent]);

  const handleFormChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (
      (!isCountryMatch && !formValues.teamId) ||
      (isCountryMatch && !formValues.country) ||
      !formValues.playerName ||
      formValues.time === ""
    ) {
      alert(
        `${isCountryMatch ? "Country" : "Team"}, player name, and time are required`,
      );
      return;
    }

    const payload = {
      time: parseInt(formValues.time),
      type: formValues.type,
      playerName: formValues.playerName,
      order: parseInt(formValues.order) || 0,
    };

    if (isCountryMatch) {
      payload.country = String(formValues.country).toUpperCase();
    } else {
      payload.teamId = formValues.teamId;
    }

    if (formValues.assistName) {
      payload.assistName = formValues.assistName;
    }

    onSubmit(payload);
  };

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {editingEvent ? "Edit Event" : "Add New Event"}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <FormControl fullWidth required>
          <InputLabel>{isCountryMatch ? "Country" : "Team"}</InputLabel>

          <Select
            value={isCountryMatch ? formValues.country : formValues.teamId}
            onChange={(e) => {
              const selected = e.target.value;
              setFormValues((prev) => ({
                ...prev,
                ...(isCountryMatch
                  ? { country: selected }
                  : { teamId: selected }),
              }));
            }}
            label={isCountryMatch ? "Country" : "Team"}
            disabled={isSubmitting}
          >
            <MenuItem
              value={isCountryMatch ? homeCountryCode || "" : homeTeam.id}
              disabled={isCountryMatch ? !homeCountryCode : !homeTeam.id}
            >
              {homeSideLabel || homeTeam.name || `Team ${homeTeam.id}`}
            </MenuItem>

            <MenuItem
              value={isCountryMatch ? awayCountryCode || "" : awayTeam.id}
              disabled={isCountryMatch ? !awayCountryCode : !awayTeam.id}
            >
              {awaySideLabel || awayTeam.name || `Team ${awayTeam.id}`}
            </MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Time (minutes)"
          type="number"
          value={formValues.time}
          onChange={(e) => handleFormChange("time", e.target.value)}
          required
          disabled={isSubmitting}
          inputProps={{ min: 0 }}
        />

        <FormControl fullWidth>
          <InputLabel>Type</InputLabel>

          <Select
            value={formValues.type}
            onChange={(e) => handleFormChange("type", e.target.value)}
            label="Type"
            disabled={isSubmitting}
          >
            <MenuItem value="goal">Goal</MenuItem>

            <MenuItem value="redCard">Red Card</MenuItem>

            <MenuItem value="penaltyMiss">Penalty (miss)</MenuItem>

            <MenuItem value="penaltyGoal">Penalty (goal)</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Player Name"
          value={formValues.playerName}
          onChange={(e) => handleFormChange("playerName", e.target.value)}
          required
          disabled={isSubmitting}
        />

        <TextField
          fullWidth
          label="Assist Name (optional)"
          value={formValues.assistName}
          onChange={(e) => handleFormChange("assistName", e.target.value)}
          disabled={isSubmitting}
        />

        <TextField
          fullWidth
          label="Order"
          type="number"
          value={formValues.order}
          onChange={(e) => handleFormChange("order", e.target.value)}
          disabled={isSubmitting}
          inputProps={{ min: 0 }}
        />

        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {editingEvent ? "Update Event" : "Create Event"}
          </Button>

          <Button variant="outlined" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default MatchEventForm;
